import { _ } from 'ajv';
import { operators, or, str } from 'ajv/dist/compile/codegen';
import { useFunc } from 'ajv/dist/compile/util';
var UidError;
(function (UidError) {
    UidError["EncodedLength"] = "encoded";
    UidError["DecodedLength"] = "decoded";
    UidError["Ambiguous"] = "ambiguous";
})(UidError || (UidError = {}));
export const error = {
    message: ({ schema, params }) => {
        if (params.error === UidError.EncodedLength) {
            return str `length must be between ${params.b64min} and ${params.b64max}, but is ${params.length}`;
        }
        if (params.error === UidError.DecodedLength) {
            return `must decode to ${schema} bytes`;
        }
        if (params.error === UidError.Ambiguous) {
            return `encoding is ambiguous`;
        }
        return `unknown uid error ${params.error}`;
    },
    params: ({ params }) => {
        if (params.error === UidError.EncodedLength) {
            return _ `{ error: ${params.error}, b64min: ${params.b64min}, b64max: ${params.b64max}, length: ${params.length} }`;
        }
        return _ `{ error: ${params.error} }`;
    }
};
export const uidv2 = {
    keyword: 'uid',
    type: 'string',
    error,
    code: (cxt) => {
        const { data, schema, gen, it } = cxt;
        if (!schema)
            return;
        const valid = gen.let('valid');
        gen.block(validate);
        cxt.ok(valid);
        function validate() {
            // check length of encoded string
            const b64min = gen.const('b64min', _ `Math.ceil(4 * ${schema} / 3)`);
            const b64max = gen.const('b64max', _ `Math.ceil(4 * ${schema} / 3) + 3`);
            const length = it.opts.unicode === false
                ? _ `${data}.length`
                : _ `${useFunc(gen, { code: 'require("ajv/dist/runtime/ucs2length").default' })}(${data})`;
            gen.if(or(_ `${length} ${operators.LT} ${b64min}`, _ `${length} ${operators.GT} ${b64max}`), () => {
                cxt.error(false, { error: UidError.EncodedLength, b64min, b64max, length });
                gen.assign(valid, false);
            }, () => {
                // check id decodes to expected length
                // failures to decode return empty buffers, they don't seem to throw errors
                const decoded = gen.const('decoded', _ `${useFunc(gen, { code: 'require("base64url").default.toBuffer' })}(${data})`);
                const decodedLength = _ `${decoded}.length`;
                gen.if(_ `${decodedLength} ${operators.NEQ} ${schema}`, () => {
                    cxt.error(false, { error: UidError.DecodedLength });
                    gen.assign(valid, false);
                }, () => {
                    // base64url decodes 'AAAAAAAAAAAAAA' and 'AAAAAAAAAAAAAK' to the same Buffer
                    // (10 bytes of 00). This is because we used a key length that isn't divisble
                    // by 3. You end up with bits at the end that are meaningless, never good.
                    // We'll leave this for now but we should move to multiples of 3 before launch.
                    // Then we can remove this check.
                    //
                    // It's not desirable to have keys that map to each other so we'll only acccept
                    // IDs that encode/decode back to their cannonical form (cannonical: with padding
                    // '=' removed)
                    const encoded = gen.const('encoded', _ `${useFunc(gen, { code: 'require("base64url").default.encode' })}(${decoded})`);
                    const trimEquals = useFunc(gen, {
                        code: String.raw `function trimEquals(id){let end=id.length-1;while(end>=0){if(id[end]!=="=")break;--end}return id.substring(0,end+1)}`
                    });
                    gen.if(_ `${trimEquals}(${encoded}) ${operators.NEQ} ${trimEquals}(${data})`, () => {
                        cxt.error(false, { error: UidError.Ambiguous });
                        gen.assign(valid, false);
                    });
                });
            });
        }
    },
    metaSchema: {
        title: 'ID bytes',
        description: 'number of bytes in ID',
        type: 'number'
    }
};
//# sourceMappingURL=uidv2.js.map